
import React, { useEffect, RefObject } from 'react';
import { Skill } from '../../types/game';

interface SkillConnectionsProps {
  canvasRef: RefObject<HTMLCanvasElement>;
  skills: Skill[];
  hasSkill: (skillId: string) => boolean;
}

const SkillConnections: React.FC<SkillConnectionsProps> = ({ 
  canvasRef, 
  skills,
  hasSkill
}) => {
  // Draw connections between skills and their prerequisites
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set line style
    ctx.strokeStyle = 'rgba(90, 168, 255, 0.3)';
    ctx.lineWidth = 2;
    
    // Find all skill elements and their positions
    const skillElements: Record<string, DOMRect> = {};
    document.querySelectorAll('[data-skill-id]').forEach(el => {
      const skillId = el.getAttribute('data-skill-id');
      if (skillId) {
        skillElements[skillId] = el.getBoundingClientRect();
      }
    });
    
    // Adjust for canvas position
    const canvasRect = canvas.getBoundingClientRect();
    
    // Draw connections
    skills.forEach(skill => {
      if (!skill.prerequisites) return;
      
      const targetEl = skillElements[skill.id];
      if (!targetEl) return;
      
      skill.prerequisites.forEach(prereqId => {
        const sourceEl = skillElements[prereqId];
        if (!sourceEl) return;
        
        // Calculate start and end points
        const startX = sourceEl.left + sourceEl.width / 2 - canvasRect.left;
        const startY = sourceEl.top + sourceEl.height - canvasRect.top;
        const endX = targetEl.left + targetEl.width / 2 - canvasRect.left;
        const endY = targetEl.top - canvasRect.top;
        
        // Draw path
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        
        // Create a curved line
        const controlX = (startX + endX) / 2;
        const controlY1 = startY + (endY - startY) / 3;
        const controlY2 = endY - (endY - startY) / 3;
        
        ctx.bezierCurveTo(controlX, controlY1, controlX, controlY2, endX, endY);
        ctx.stroke();
        
        // Add arrow
        const arrowSize = 6;
        const angle = Math.atan2(endY - controlY2, endX - controlX);
        
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - arrowSize * Math.cos(angle - Math.PI / 6),
          endY - arrowSize * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          endX - arrowSize * Math.cos(angle + Math.PI / 6),
          endY - arrowSize * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fillStyle = 'rgba(90, 168, 255, 0.5)';
        ctx.fill();
      });
    });
  }, [skills, hasSkill, canvasRef]);

  return (
    <canvas 
      ref={canvasRef} 
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
    />
  );
};

export default SkillConnections;
